import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Heart, Users, Flower, ShoppingBag, Sparkle } from '@phosphor-icons/react';
import { Button } from '@/components/ui/button';

export default function Landing() {
  const navigate = useNavigate();
  
  const handleJoinNow = () => {
    navigate('/auth');
  };

  return (
    <div className="min-h-screen bg-background">
      <nav className="fixed top-0 w-full z-50 bg-white/70 backdrop-blur-lg border-b border-white/20">
        <div className="max-w-7xl mx-auto px-6 py-4 flex justify-between items-center">
          <div className="flex items-center gap-2">
            <Flower size={32} weight="fill" className="text-primary" />
            <span className="text-2xl font-heading font-bold text-foreground">Bloom & Bond</span>
          </div>
          <Button 
            data-testid="nav-join-button"
            onClick={handleJoinNow}
            className="rounded-full bg-primary hover:bg-primary-hover text-white font-bold px-8 shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-1"
          >
            Join Now
          </Button>
        </div>
      </nav>

      <section className="pt-32 pb-20 px-6">
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <h1 className="text-5xl md:text-7xl font-heading font-bold tracking-tight leading-none mb-6 bg-gradient-to-r from-primary to-secondary bg-clip-text text-transparent">
              Connect. Share. Bloom.
            </h1>
            <p className="text-base md:text-lg leading-relaxed text-muted-foreground max-w-2xl mx-auto mb-8">
              A vibrant community for women to chat, share moments, exchange flowers, 
              and support each other's growth.
            </p>
            <Button 
              data-testid="hero-join-button"
              onClick={handleJoinNow}
              size="lg"
              className="rounded-full bg-gradient-to-r from-primary to-[#FF8E53] hover:shadow-xl text-white font-bold px-12 py-6 text-lg transition-all duration-300 hover:-translate-y-1 active:scale-95"
            >
              <Sparkle size={24} weight="fill" className="mr-2" />
              Join Our Community
            </Button>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-20"
          >
            <div className="relative rounded-3xl overflow-hidden h-96">
              <img 
                src="https://images.unsplash.com/photo-1597736960442-d613cca349db?crop=entropy&cs=srgb&fm=jpg&q=85" 
                alt="Women community gathering"
                className="w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent flex items-end p-8">
                <h3 className="text-white text-2xl font-heading font-bold">Connect & Share</h3>
              </div>
            </div>
            <div className="relative rounded-3xl overflow-hidden h-96">
              <img 
                src="https://images.unsplash.com/photo-1761156254716-4d52c56020e3?crop=entropy&cs=srgb&fm=jpg&q=85" 
                alt="Beautiful flower arrangement"
                className="w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent flex items-end p-8">
                <h3 className="text-white text-2xl font-heading font-bold">Flower Exchange</h3>
              </div>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.8, delay: 0.4 }}
            className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-20"
          >
            <div className="bg-white/80 backdrop-blur-md border border-white/20 shadow-sm rounded-2xl p-8 hover:shadow-md transition-all duration-300">
              <Users size={48} weight="fill" className="text-primary mb-4" />
              <h3 className="text-2xl font-heading font-medium mb-3">Real-Time Chat</h3>
              <p className="text-muted-foreground">Connect with members through our vibrant chat room. Share thoughts, photos, and support.</p>
            </div>
            <div className="bg-white/80 backdrop-blur-md border border-white/20 shadow-sm rounded-2xl p-8 hover:shadow-md transition-all duration-300">
              <Flower size={48} weight="fill" className="text-secondary" />
              <h3 className="text-2xl font-heading font-medium mb-3">Flower Exchange</h3>
              <p className="text-muted-foreground">Join our yearly tradition of spreading joy through floral arrangements with fellow members.</p>
            </div>
            <div className="bg-white/80 backdrop-blur-md border border-white/20 shadow-sm rounded-2xl p-8 hover:shadow-md transition-all duration-300">
              <ShoppingBag size={48} weight="fill" className="text-accent" />
              <h3 className="text-2xl font-heading font-medium mb-3">Support & Merch</h3>
              <p className="text-muted-foreground">Donate to our cause and shop exclusive community merchandise to show your support.</p>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.6 }}
            className="bg-gradient-to-br from-[#FF9A9E] to-[#FECFEF] rounded-3xl p-12 text-center"
          >
            <Heart size={64} weight="fill" className="text-white mx-auto mb-6" />
            <h2 className="text-4xl md:text-5xl font-heading font-semibold text-white mb-4">
              Ready to Bloom?
            </h2>
            <p className="text-white/90 text-lg mb-8 max-w-2xl mx-auto">
              Join hundreds of women building meaningful connections and spreading kindness.
            </p>
            <Button 
              data-testid="cta-join-button"
              onClick={handleJoinNow}
              size="lg"
              className="rounded-full bg-white text-primary hover:bg-white/90 font-bold px-12 py-6 text-lg shadow-xl transition-all duration-300 hover:-translate-y-1 active:scale-95"
            >
              Get Started Today
            </Button>
          </motion.div>
        </div>
      </section>

      <footer className="bg-white border-t border-border py-12 px-6">
        <div className="max-w-7xl mx-auto text-center text-muted-foreground">
          <div className="flex items-center justify-center gap-2 mb-4">
            <Flower size={24} weight="fill" className="text-primary" />
            <span className="font-heading font-bold text-foreground">Bloom & Bond</span>
          </div>
          <p className="text-sm">© 2026 Bloom & Bond. A community for women, by women.</p>
        </div>
      </footer>
    </div>
  );
}